import moment from "moment"
import React from "react"
import {connect} from "react-redux"
import ComponentWithData from "~/utils/ComponentWithData"
import {Article} from "~/utils/ShelfEntities"
import {formatOptionalNumber} from "~/utils"

import {
  ArticleDocumentForm,
  BarcodeDetailInfoRow,
  Button,
  DetailInfoRow,
  List,
  ListItemDocument,
  ListItemProduct,
  ListItemStoragePlace,
  Viscosity
} from "~/components/"

import {withTranslation} from "react-i18next"
import ShelfApi from "../../utils/ShelfApi"

const icon_lost = require("~/icons/stock_out_32_blue.svg")
const icon_movement = require("~/icons/movement_32_blue.svg")
const icon_refill = require("~/icons/stock_up_32_blue.svg")

class ArticleDetailView extends ComponentWithData {
  constructor(props) {
    super(props)

    this.state.loading = false
  }

  getData() {
    return Article.find(this.props.match.params.article_barcode)
  }

  renderLoading() {
    return <p>{this.props.t("articles.detail.loading")}</p>
  }

  renderError() {
    return (
      <p>
        {this.props.t("articles.detail.error")} {this.state.error}
      </p>
    )
  }

  renderEmpty() {
    return <p>{this.props.t("articles.detail.notFound")}</p>
  }

  renderWithData() {
    const {
      barcode,
      expiry_date,
      note,
      viscosity,
      product,
      stock_out_destinations,
      stocks,
      current_article_inventories,
      current_quantity,
      is_singular_item,
      lot_number,
      document_attachments,
      serial_number
    } = this.state.data
    const canEditArticle = this.props.tenantAdmin || this.props.admin
    const usingLotNumber = this.props.usingLotNumber

    const stockedIn = stocks.some(
      stock => stock.storage_place && stock.storage_place.type === "Location"
    )

    const storagePlace = stocks
      .map(stock => stock.storage_place)
      .filter(
        storage_place => storage_place && storage_place.type === "Location"
      )[0]

    const totalQuantity = stocks.reduce((sum, stock) => sum + stock.quantity, 0)

    const handleGenerateArticleAudit = async () => {
      await ShelfApi.post("/article_audit_documents", {
        article_audit_document: {
          article_barcode: barcode,
          location_barcode: storagePlace.barcode,
          action: "idle"
        }
      })
      this.reloadData()
    }

    return (
      <div className="sh-detail-view">
        <div className="header">
          <div className="title">{this.props.t("articles.detail.title")}</div>
          {canEditArticle && (
            <div className="actions">
              {stockedIn && (
                <React.Fragment>
                  <Button
                    secondary
                    to={"/articles/move/" + barcode}
                    icon={icon_movement}
                    label={this.props.t("articles.detail.move")}
                  />
                </React.Fragment>
              )}
              {stockedIn && !!!is_singular_item && (
                <React.Fragment>
                  <Button
                    secondary
                    to={"/articles/stock_up/" + barcode}
                    icon={icon_refill}
                    label={this.props.t("articles.detail.stockUp")}
                  />
                </React.Fragment>
              )}
              {stockedIn && (
                <React.Fragment>
                  <Button
                    secondary
                    to={"/articles/stock_out/" + barcode}
                    icon={icon_lost}
                    label={this.props.t("articles.detail.stockOut")}
                  />
                </React.Fragment>
              )}
              <Button
                primary
                to={"/articles/" + barcode}
                label={this.props.t("articles.detail.edit")}
                style={{marginLeft: 20}}
              />
            </div>
          )}
        </div>

        <div>
          <BarcodeDetailInfoRow value={barcode} />
          <DetailInfoRow
            label={this.props.t("articles.detail.expiryDate")}
            value={expiry_date && moment(expiry_date).format("DD.MM.YYYY")}
          />
          {usingLotNumber && (
            <DetailInfoRow
              label={this.props.t("articles.detail.lotNumber")}
              value={lot_number}
            />
          )}
          <DetailInfoRow
            label={this.props.t("articles.detail.quantity")}
            value={`${current_quantity} ${product.unit ? product.unit : ""}`}
          />

          {this.props.usingViscosity && (
            <DetailInfoRow
              label={this.props.t("articles.detail.viscosity")}
              value={<Viscosity value={viscosity} />}
            />
          )}

          <DetailInfoRow label={this.props.t("articles.detail.product")}>
            <List items={[product]} itemRenderer={ListItemProduct}>
              <div className="empty">
                {this.props.t("articles.detail.noProducts")}
              </div>
            </List>
          </DetailInfoRow>

          {!!stocks.filter(
            stock => stock.storage_place && stock.storage_place.type !== ""
          ).length && (
            <DetailInfoRow label={this.props.t("articles.detail.stock")}>
              <List
                items={
                  stocks
                    ? stocks
                        .filter(
                          stock => stock.storage_place.type === "Location"
                        )
                        .map(stock => ({
                          ...stock,
                          inventories: current_article_inventories.find(
                            ai => ai.location_id === stock.storage_place.id
                          )
                        }))
                    : []
                }
                itemRenderer={ListItemStoragePlace}
              >
                <div className="empty">
                  {this.props.t("articles.detail.notStockedIn")}
                </div>
              </List>
            </DetailInfoRow>
          )}

          <DetailInfoRow
            label={this.props.t("articles.detail.note")}
            value={note}
          />
          <DetailInfoRow
            label={this.props.t("articles.detail.serialNumber")}
            value={serial_number}
          />
          {!is_singular_item && product && (
            <React.Fragment>
              <DetailInfoRow
                label={this.props.t("articles.detail.reorderQuantity")}
                value={formatOptionalNumber(product.reorder_quantity)}
              />
              <DetailInfoRow
                label={this.props.t("articles.detail.targetQuantity")}
                value={formatOptionalNumber(product.target_quantity)}
              />
              <DetailInfoRow
                label={this.props.t("articles.detail.reorderUnitQuantity")}
                value={formatOptionalNumber(product.reorder_unit_quantity)}
              />
            </React.Fragment>
          )}
          {this.props.usingArticleDocuments && (
            <React.Fragment>
              <DetailInfoRow label={this.props.t("articles.detail.documents")}>
                {storagePlace && (
                  <Button
                    onClick={() => {
                      this.setState({loading: true})
                      handleGenerateArticleAudit()
                    }}
                    disabled={this.state.loading}
                    primary
                  >
                    {this.props.t("articles.detail.generateAuditDocument")}
                  </Button>
                )}
                <List
                  items={document_attachments}
                  itemRenderer={ListItemDocument}
                >
                  <div className="empty">
                    {this.props.t("articles.detail.noDocuments")}
                  </div>
                </List>
              </DetailInfoRow>
              <ArticleDocumentForm barcode={barcode} />
            </React.Fragment>
          )}
        </div>
      </div>
    )
  }
}

const mapStateToProps = ({auth}) => {
  const {user} = auth
  const {settings, admin, tenantAdmin} = user
  const {
    usingViscosity,
    usingTestDate,
    usingArticleDocuments,
    usingLotNumber
  } = settings
  return {
    usingViscosity,
    usingTestDate,
    usingArticleDocuments,
    usingLotNumber,
    admin,
    tenantAdmin
  }
}

export default withTranslation()(connect(mapStateToProps)(ArticleDetailView))



// WEBPACK FOOTER //
// ./src/views/detail/ArticleDetailView.js